import { channelToWorkflowTypes } from 'src/pages/workflow-builder-edit/constants';
import { WorkflowTypes } from 'src/pages/workflow-builder-edit/types';
import { TranslationChannel } from 'src/types/workflowBuilderAPITypes';
import { CommonIntentWorkflowType } from 'src/utils/enums';

export function isIntentDuplicable(intentId: string) {
  return (
    intentId !== CommonIntentWorkflowType.KNOWLEDGE_ARTICLE &&
    intentId !== CommonIntentWorkflowType.FALLBACK
  );
}

/**
 * When `classic_workflows_deprecated` is on, an intent should be treated as
 * autoflow unless it's FALLBACK or KNOWLEDGE_ARTICLE (which always stay
 * classic). Otherwise falls back to the intent's actual `is_autoflow`.
 *
 * This is the single source of truth for the FF-gated rule. The
 * `getEffectiveWorkflowMode` helper below wraps this for navigation use
 * cases that need a `'autoflow' | 'classic'` mode string.
 */
export function isEffectivelyAutoflow(
  intentId: string,
  isAutoflow: boolean | undefined,
  isClassicWorkflowsDeprecated: boolean,
): boolean {
  if (isClassicWorkflowsDeprecated && isIntentDuplicable(intentId)) {
    return true;
  }
  return Boolean(isAutoflow);
}

/**
 * Returns the workflow mode for navigation/URL purposes — `'autoflow'` or
 * `'classic'`. Thin wrapper around `isEffectivelyAutoflow`.
 */
export function getEffectiveWorkflowMode(
  intentId: string,
  isAutoflow: boolean | undefined,
  isClassicWorkflowsDeprecated: boolean,
): 'autoflow' | 'classic' {
  return isEffectivelyAutoflow(
    intentId,
    isAutoflow,
    isClassicWorkflowsDeprecated,
  )
    ? 'autoflow'
    : 'classic';
}

/**
 * Single source of truth for the routing card / reroute tool badge text:
 * "Subagent - Autoflows", "Subagent - Classic", "Agent - Autoflows",
 * "Agent - Classic". When classic_workflows_deprecated is on, every
 * routable item renders as autoflow (reroute tools are always duplicable;
 * for routable intents callers should pre-filter FALLBACK / KNOWLEDGE_ARTICLE
 * which never appear in routing surfaces).
 */
export function getRouteBadgeLabel(
  isHandoff: boolean,
  isAutoflow: boolean | undefined,
  isClassicWorkflowsDeprecated: boolean,
): string {
  const treatAsAutoflow = Boolean(isAutoflow) || isClassicWorkflowsDeprecated;
  const suffix = treatAsAutoflow ? 'Autoflows' : 'Classic';
  return `${isHandoff ? 'Subagent' : 'Agent'} - ${suffix}`;
}

export function isIntentEditable(intentId: string) {
  return !(
    intentId === CommonIntentWorkflowType.GENERAL_HANDOFF ||
    intentId === CommonIntentWorkflowType.KNOWLEDGE_ARTICLE ||
    intentId === CommonIntentWorkflowType.FALLBACK
  );
}

export function isOnlyInteractiveEmail(intentId: string) {
  return (
    intentId === CommonIntentWorkflowType.HANDOFF ||
    intentId === CommonIntentWorkflowType.FALLBACK
  );
}

export function isIntentActiveForChannel(
  activeWorkflowTypes: WorkflowTypes[],
  channel: TranslationChannel,
) {
  const liveWorkflowTypesForChannel = channelToWorkflowTypes[channel];

  return activeWorkflowTypes.some(activeWorkflowType =>
    liveWorkflowTypesForChannel.includes(activeWorkflowType),
  );
}

export const getWorkflowActivationCopy = ({
  channel,
  hasOtherQuestions,
  intentName,
  isActive,
  isKnowledgeArticle,
}: {
  channel: string;
  hasOtherQuestions: boolean;
  intentName?: string;
  isActive: boolean;
  isKnowledgeArticle: boolean;
}) => {
  const knowledgeArticleMessage = `. ${
    isActive
      ? 'Disabling Knowledge Retrieval activates Other Questions.'
      : 'Enabling Knowledge Retrieval replaces Other Questions.'
  }`;

  const label = `${isActive ? 'Deactivate' : 'Activate'} ${
    intentName ? `${intentName} ` : ' '
  }agent`;

  const channelName = channel.toLowerCase() === 'playbook' ? 'Assist' : channel;
  const confirmationMessage = `This will make your agent ${
    isActive ? 'go offline' : 'go live'
  } for the ${channelName} channel`;

  return {
    confirmationMessage:
      isKnowledgeArticle && hasOtherQuestions
        ? confirmationMessage + knowledgeArticleMessage
        : confirmationMessage,
    label,
  };
};
